import React, { useState, useEffect, useCallback } from 'react';
import ReactFlow, { 
  Background, 
  Controls, 
  MiniMap,
  Handle,
  Position,
  MarkerType,
  ReactFlowProvider,
  useNodesState,
  useEdgesState
} from 'reactflow';
import 'reactflow/dist/style.css';

const TurintechAutoFeedback = () => {
  const [selectedMethod, setSelectedMethod] = useState('nsga2');

  // Load saved positions from localStorage
  const loadSavedPositions = () => {
    const saved = localStorage.getItem('turintech-auto-feedback-positions');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        console.error('Failed to parse saved positions:', e);
      }
    }
    return null;
  };

  const savedPositions = loadSavedPositions();

  const initialNodes = [
    // Input Section Header
    {
      id: 'input-header',
      data: { 
        label: (
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <div style={{ 
              fontSize: '28px', 
              fontWeight: 'bold',
              color: '#F97316',
              marginBottom: '10px'
            }}>
              🏁 START HERE
            </div>
            <div style={{ fontSize: '16px', color: '#FED7AA' }}>
              Input your code and optimization goals
            </div>
          </div>
        )
      },
      position: savedPositions?.['input-header'] || { x: 350, y: 10 },
      style: {
        background: 'linear-gradient(135deg, #EA580C 0%, #C2410C 100%)',
        border: '3px solid #F97316',
        borderRadius: '16px',
        width: 400,
        boxShadow: '0 8px 24px rgba(234, 88, 12, 0.4)'
      }
    },

    // Code Input
    {
      id: 'code-input',
      data: { 
        label: (
          <div style={{ padding: '25px', textAlign: 'left' }}>
            <strong style={{ fontSize: '24px', color: '#FFFFFF' }}>📝 Your Code</strong>
            <div style={{
              background: 'rgba(0,0,0,0.6)',
              borderRadius: '8px',
              padding: '20px',
              marginTop: '15px',
              fontFamily: 'monospace',
              fontSize: '16px',
              color: '#E5E7EB',
              lineHeight: '1.8',
              border: '1px solid #4B5563'
            }}>
              <pre style={{ margin: 0, whiteSpace: 'pre-wrap' }}>
                {`def process_data(data):
  results = []
  for item in data:
    # complex calculation...
    results.append(item * 2)
  return results`}
              </pre>
            </div>
          </div>
        )
      },
      position: savedPositions?.['code-input'] || { x: 80, y: 120 },
      style: {
        background: '#1F2937',
        border: '2px solid #374151',
        borderRadius: '12px',
        width: 440,
        color: 'white',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
      }
    },
    
    // User Goal
    {
      id: 'user-goal',
      data: { 
        label: (
          <div style={{ padding: '30px', textAlign: 'left' }}>
            <strong style={{ fontSize: '26px', color: '#FFFFFF' }}>🎯 Your Goal</strong>
            <div style={{ fontSize: '20px', marginTop: '25px', color: '#E0F2FE', lineHeight: '1.8' }}>
              "Make this function <strong style={{ color: '#38BDF8' }}>10x faster</strong> and <br/>
              <strong style={{ color: '#38BDF8' }}>reduce memory usage</strong>."
            </div>
          </div>
        )
      },
      position: savedPositions?.['user-goal'] || { x: 560, y: 120 },
      style: {
        background: 'linear-gradient(135deg, #0284C7 0%, #0E7490 100%)',
        border: '2px solid #0EA5E9',
        borderRadius: '12px',
        width: 440,
        height: 220,
        color: 'white',
        boxShadow: '0 4px 12px rgba(2, 132, 199, 0.3)'
      }
    },

    // Feedback Sources Header
    {
      id: 'feedback-header',
      data: { 
        label: (
          <div style={{ padding: '15px', textAlign: 'center' }}>
            <strong style={{ fontSize: '22px', color: '#818CF8' }}>📥 Feedback Sources</strong>
          </div>
        )
      },
      position: savedPositions?.['feedback-header'] || { x: 450, y: 380 },
      style: {
        background: 'transparent',
        border: 'none',
        fontSize: '22px',
        fontWeight: 'bold',
        color: '#818CF8'
      }
    },
    
    // Human Feedback - Chat Style
    {
      id: 'human-feedback',
      data: { 
        label: (
          <div style={{ padding: '25px', textAlign: 'left' }}>
            <strong style={{ fontSize: '24px', color: '#FFFFFF', marginBottom: '20px', display: 'block' }}>
              👤 Human Feedback
            </strong>
            <div style={{ 
              background: 'rgba(0,0,0,0.3)', 
              borderRadius: '12px', 
              padding: '20px',
              fontSize: '16px'
            }}>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: '#93C5FD', fontSize: '14px', marginBottom: '5px' }}>Developer:</div>
                <div style={{ 
                  background: '#1E40AF', 
                  padding: '12px 16px', 
                  borderRadius: '8px',
                  color: '#DBEAFE'
                }}>
                  "Make this function 10x faster"
                </div>
              </div>
              <div style={{ marginBottom: '15px' }}>
                <div style={{ color: '#93C5FD', fontSize: '14px', marginBottom: '5px' }}>Team Lead:</div>
                <div style={{ 
                  background: '#1E40AF', 
                  padding: '12px 16px', 
                  borderRadius: '8px',
                  color: '#DBEAFE'
                }}>
                  "Memory usage is critical, we're hitting limits"
                </div>
              </div>
              <div>
                <div style={{ color: '#93C5FD', fontSize: '14px', marginBottom: '5px' }}>Junior Dev:</div>
                <div style={{ 
                  background: '#1E40AF', 
                  padding: '12px 16px', 
                  borderRadius: '8px',
                  color: '#DBEAFE'
                }}>
                  "Code is too complex to understand"
                </div>
              </div>
            </div>
          </div>
        )
      },
      position: savedPositions?.['human-feedback'] || { x: 50, y: 450 },
      style: {
        background: '#2563EB',
        border: '2px solid #3B82F6',
        borderRadius: '12px',
        width: 320,
        color: 'white',
        boxShadow: '0 4px 12px rgba(37, 99, 235, 0.3)'
      }
    },
    
    // AI Feedback with Scores
    {
      id: 'ai-feedback',
      data: { 
        label: (
          <div style={{ padding: '25px', textAlign: 'left' }}>
            <strong style={{ fontSize: '24px', color: '#FFFFFF', marginBottom: '20px', display: 'block' }}>
              🤖 AI Feedback (Judges)
            </strong>
            <div style={{ fontSize: '16px', lineHeight: '2' }}>
              <div style={{ 
                background: 'rgba(0,0,0,0.3)', 
                padding: '15px', 
                borderRadius: '8px',
                marginBottom: '10px'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>• "Loop can be parallelized"</span>
                  <span style={{ color: '#FCD34D', fontWeight: 'bold' }}>+4.5</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>• "Database queries not optimized"</span>
                  <span style={{ color: '#F87171', fontWeight: 'bold' }}>+3.0</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span>• "Missing error handling"</span>
                  <span style={{ color: '#FB923C', fontWeight: 'bold' }}>+2.5</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                  <span>• "Potential memory leak"</span>
                  <span style={{ color: '#F87171', fontWeight: 'bold' }}>+4.0</span>
                </div>
              </div>
              <div style={{ 
                textAlign: 'center', 
                marginTop: '15px',
                padding: '10px',
                background: 'rgba(168, 85, 247, 0.2)',
                borderRadius: '8px'
              }}>
                <strong>Total AI Score: 14.0</strong>
              </div>
            </div>
          </div>
        )
      },
      position: savedPositions?.['ai-feedback'] || { x: 390, y: 450 },
      style: {
        background: '#7C3AED',
        border: '2px solid #9333EA',
        borderRadius: '12px',
        width: 320,
        color: 'white',
        boxShadow: '0 4px 12px rgba(124, 58, 237, 0.3)'
      }
    },
    
    // System Feedback - Terminal Style
    {
      id: 'system-feedback',
      data: { 
        label: (
          <div style={{ padding: '25px', textAlign: 'left' }}>
            <strong style={{ fontSize: '24px', color: '#FFFFFF', marginBottom: '20px', display: 'block' }}>
              ⚙️ System Feedback
            </strong>
            <div style={{
              background: '#000000',
              borderRadius: '8px',
              padding: '20px',
              fontFamily: 'monospace',
              fontSize: '15px',
              lineHeight: '1.8',
              border: '1px solid #10B981'
            }}>
              <div style={{ color: '#10B981' }}>$ npm test</div>
              <div style={{ color: '#6EE7B7' }}>✓ Unit Tests: 142/150 passing</div>
              <div style={{ color: '#F87171' }}>✗ 8 tests failed</div>
              <div style={{ marginTop: '10px', color: '#10B981' }}>$ performance-monitor</div>
              <div style={{ color: '#FCD34D' }}>⚡ API Response: 245ms → 89ms</div>
              <div style={{ color: '#34D399' }}>💾 Memory: 512MB → 128MB</div>
              <div style={{ marginTop: '10px', color: '#10B981' }}>$ coverage report</div>
              <div style={{ color: '#6EE7B7' }}>📊 Coverage: 78% → 92%</div>
            </div>
          </div>
        )
      },
      position: savedPositions?.['system-feedback'] || { x: 730, y: 450 },
      style: {
        background: '#064E3B',
        border: '2px solid #10B981',
        borderRadius: '12px',
        width: 320,
        color: 'white',
        boxShadow: '0 4px 12px rgba(5, 150, 105, 0.3)'
      }
    },
    
    // Scoring System
    {
      id: 'scoring-system',
      data: { 
        label: (
          <div style={{ padding: '35px', textAlign: 'center' }}>
            <strong style={{ fontSize: '28px', color: '#FFFFFF' }}>📊 Intelligent Scoring System</strong>
            <div style={{ fontSize: '18px', marginTop: '30px', color: '#FFF' }}>
              <div style={{ display: 'flex', gap: '40px', justifyContent: 'space-between' }}>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <strong style={{ color: '#FCD34D', fontSize: '20px' }}>Qualitative → Score:</strong><br/>
                  <div style={{ fontSize: '17px', marginTop: '15px', lineHeight: '2' }}>
                    "Make it faster" → Speed: 5/5<br/>
                    "Too complex" → Readability: 2/5<br/>
                    "Add caching" → Performance: 4/5
                  </div>
                </div>
                <div style={{ flex: 1, textAlign: 'left' }}>
                  <strong style={{ color: '#FCD34D', fontSize: '20px' }}>Quantitative → Values:</strong><br/>
                  <div style={{ fontSize: '17px', marginTop: '15px', lineHeight: '2' }}>
                    Response Time: 89ms<br/>
                    Memory Usage: 128MB<br/>
                    Test Pass Rate: 94.7%
                  </div>
                </div>
              </div>
              <div style={{ 
                marginTop: '30px', 
                padding: '25px', 
                background: 'rgba(0, 0, 0, 0.5)', 
                borderRadius: '12px',
                border: '2px solid rgba(252, 211, 77, 0.5)'
              }}>
                <strong style={{ fontSize: '20px', color: '#FCD34D' }}>Unified Objective Function:</strong>
                <div style={{ 
                  fontFamily: 'monospace', 
                  fontSize: '18px', 
                  marginTop: '20px',
                  color: '#FFFFFF',
                  background: 'rgba(0, 0, 0, 0.7)',
                  padding: '20px',
                  borderRadius: '8px',
                  lineHeight: '1.8'
                }}>
                  <div style={{ color: '#60A5FA', marginBottom: '15px' }}>
                    f(x) = w₁·speed + w₂·memory + w₃·readability + w₄·tests
                  </div>
                  <div style={{ color: '#FCD34D' }}>
                    Vector = [5, 4, 2, 5, 89ms, 128MB, 94.7%]
                  </div>
                  <div style={{ color: '#34D399', marginTop: '15px', fontSize: '16px' }}>
                    Optimized Score = 0.85 (Higher is better)
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      },
      position: savedPositions?.['scoring-system'] || { x: 200, y: 800 },
      style: {
        background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)',
        border: '3px solid #6D28D9',
        borderRadius: '16px',
        width: 600,
        color: 'white',
        boxShadow: '0 8px 24px rgba(79, 70, 229, 0.3)'
      }
    },
    
    // Next Steps Indicator
    {
      id: 'next-steps',
      data: { 
        label: (
          <div style={{ padding: '25px', textAlign: 'center' }}>
            <strong style={{ fontSize: '20px', color: '#FFFFFF' }}>➡️ Next: Optimization Approaches</strong>
            <div style={{ fontSize: '16px', marginTop: '15px', color: '#E0E7FF', lineHeight: '1.8' }}>
              This unified objective vector feeds into various<br/>
              optimization methods to generate better code.<br/>
              <span style={{ color: '#A5B4FC', fontStyle: 'italic' }}>
                See "Optimization Approaches" tab for details
              </span>
            </div>
          </div>
        )
      },
      position: savedPositions?.['next-steps'] || { x: 300, y: 950 },
      style: {
        background: 'linear-gradient(135deg, #4338CA 0%, #3730A3 100%)',
        border: '2px dashed #6366F1',
        borderRadius: '12px',
        width: 400,
        color: 'white',
        boxShadow: '0 4px 12px rgba(67, 56, 202, 0.3)'
      }
    }
  ];

  const initialEdges = [
    // Input to Feedback
    {
      id: 'e-input-human',
      source: 'user-goal',
      target: 'human-feedback',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#0EA5E9', strokeWidth: 2, strokeDasharray: '5 5' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#0EA5E9' }
    },
    {
      id: 'e-input-ai',
      source: 'code-input',
      target: 'ai-feedback',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#A855F7', strokeWidth: 2, strokeDasharray: '5 5' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#A855F7' }
    },
    {
      id: 'e-input-system',
      source: 'code-input',
      target: 'system-feedback',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#10B981', strokeWidth: 2, strokeDasharray: '5 5' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#10B981' }
    },

    // Feedback to Scoring
    {
      id: 'e1',
      source: 'human-feedback',
      target: 'scoring-system',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#60A5FA', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#60A5FA' }
    },
    {
      id: 'e2',
      source: 'ai-feedback',
      target: 'scoring-system',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#C084FC', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#C084FC' }
    },
    {
      id: 'e3',
      source: 'system-feedback',
      target: 'scoring-system',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#34D399', strokeWidth: 2 },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#34D399' }
    },
    
    // Scoring to Next Steps
    {
      id: 'e4',
      source: 'scoring-system',
      target: 'next-steps',
      type: 'smoothstep',
      animated: true,
      style: { stroke: '#818CF8', strokeWidth: 3, strokeDasharray: '5 5' },
      markerEnd: { type: MarkerType.ArrowClosed, color: '#818CF8' }
    }
  ];

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  // Save positions whenever nodes change
  const handleNodesChange = useCallback((changes) => {
    onNodesChange(changes);
    
    // Save positions to localStorage
    setNodes((nds) => {
      const positions = {};
      nds.forEach(node => {
        positions[node.id] = node.position;
      });
      localStorage.setItem('turintech-auto-feedback-positions', JSON.stringify(positions));
      return nds;
    });
  }, [onNodesChange, setNodes]);

  const onNodeClick = (event, node) => {
    // Remove the optimization method selection logic
  };

  // Add reset button handler
  const handleResetPositions = () => {
    localStorage.removeItem('turintech-auto-feedback-positions');
    window.location.reload();
  };

  return (
    <ReactFlowProvider>
      <div style={{
        height: '100vh',
        background: 'linear-gradient(135deg, #0F2027 0%, #203A43 50%, #2C5364 100%)',
        display: 'flex',
        flexDirection: 'column',
        color: 'white',
        fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, sans-serif',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px 30px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          background: 'rgba(0, 0, 0, 0.3)'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h1 style={{ 
                margin: 0, 
                fontSize: '28px',
                fontWeight: '700',
                background: 'linear-gradient(135deg, #EA580C, #FF6D00)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>
                🔄 Artemis Auto-Feedback System
              </h1>
              <p style={{ margin: '5px 0 0 0', fontSize: '14px', opacity: 0.8 }}>
                Transform your code and goals into a unified objective vector for optimization
              </p>
            </div>
            
            {/* Reset Button */}
            <button
              onClick={handleResetPositions}
              style={{
                padding: '8px 16px',
                background: 'rgba(255, 255, 255, 0.1)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                borderRadius: '8px',
                color: 'white',
                fontSize: '13px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.2)';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'rgba(255, 255, 255, 0.1)';
              }}
            >
              🔄 Reset Layout
            </button>
          </div>
        </div>

        {/* Main Content */}
        <div style={{ flex: 1, position: 'relative' }}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={handleNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            fitView
            fitViewOptions={{
              padding: 0.4,
              minZoom: 0.5,
              maxZoom: 1.2
            }}
            defaultViewport={{ x: 0, y: 0, zoom: 0.8 }}
            attributionPosition="bottom-left"
            nodesDraggable={true}
            nodesConnectable={false}
            elementsSelectable={true}
          >
            <Background 
              color="rgba(255, 255, 255, 0.05)" 
              gap={20} 
              size={1}
            />
            <Controls 
              style={{
                button: {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  color: 'white'
                }
              }}
            />
            <MiniMap 
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.3)',
                border: '1px solid rgba(255, 255, 255, 0.2)'
              }}
              nodeColor={(node) => {
                if (node.style?.background?.includes('gradient')) {
                  return '#F4511E';
                }
                return 'rgba(255, 255, 255, 0.2)';
              }}
              maskColor="rgba(0, 0, 0, 0.7)"
            />
          </ReactFlow>
        </div>

        {/* Bottom Info Bar */}
        <div style={{
          padding: '15px 30px',
          background: 'rgba(0, 0, 0, 0.3)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          display: 'flex',
          justifyContent: 'center',
          gap: '30px',
          fontSize: '13px'
        }}>
          <div style={{ opacity: 0.8 }}>
            <span style={{ color: '#60A5FA', fontWeight: '600' }}>💬 Human Feedback</span> • 
            <span style={{ marginLeft: '8px' }}>Describe your goals in plain English</span>
          </div>
          <div style={{ opacity: 0.8 }}>
            <span style={{ color: '#A855F7', fontWeight: '600' }}>🤖 AI Analysis</span> • 
            <span style={{ marginLeft: '8px' }}>Automated code review and suggestions</span>
          </div>
          <div style={{ opacity: 0.8 }}>
            <span style={{ color: '#10B981', fontWeight: '600' }}>⚙️ System Metrics</span> • 
            <span style={{ marginLeft: '8px' }}>Real performance data</span>
          </div>
        </div>
      </div>
    </ReactFlowProvider>
  );
};

export default TurintechAutoFeedback; 